import { useCommonTracking } from 'common-tracking';
import { validateEmail } from 'common-utils';
import {
  Box,
  Button,
  Input,
  P,
  rem,
  Spacer,
  styled,
  theme,
  useToaster,
} from 'design-system';
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useAuthContext } from '../auth-context';
import { EnhancedLinkButton } from '../components/enhanced-link-button';
import { AUTH_TOKEN_KEY } from '../constants';
import { useForgotPassword } from '../forgot-password';
import { Wiring } from '../pages/auth-page-utility';
import { requestOtp } from '../sso';

type Props = {
  wiring: {
    goToLogin: Wiring;
  };
  onSignIn?: () => void;
  checkFirstTimeUse?: (token: string) => void;
  email?: string;
  defaultEmailValue?: string;
  defaultPF?: boolean;
};

export const ForgotPasswordForm: FC<Props> = ({
  email,
  onSignIn,
  wiring,
  checkFirstTimeUse,
  defaultEmailValue,
  defaultPF,
}) => {
  const { trackCommonEvent } = useCommonTracking();
  const { showToast } = useToaster();
  const {
    authState,
    editage: { baseUrl, domain },
    listeners: { onUserCreated },
  } = useAuthContext();
  const { forgotPasswordState, logIn, getOtp } = useForgotPassword({
    baseUrl: baseUrl,
    domain: domain,
    on: {
      loggedIn: (ssoUserId) => {
        trackCommonEvent({ type: 'login-in', userId: ssoUserId });
        onSignIn?.();
        authState.invalidate();
      },
      tokenObtained: (token, ssoUserId) => {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem('ssoUserId', ssoUserId.toString());
      },
    },
  });
  const getOtpForm = useForm<{ email: string }>({
    defaultValues: { email: defaultEmailValue ?? email },
  });
  const logInForm = useForm<{ otp: string }>({});
  const [otpError, setOtpError] = useState<string | undefined>(undefined);
  const [lastEmail, setLastEmail] = useState(defaultEmailValue ?? email);

  useEffect(() => {
    const subscription = getOtpForm.watch((values) => {
      if (values.email) {
        setLastEmail(values.email);
      }
    });
    return () => subscription.unsubscribe();
  }, [getOtpForm]);

  const handleGetOtp = getOtpForm.handleSubmit((values) => {
    const normalizedEmail = values.email.toLowerCase().trim();
    setLastEmail(normalizedEmail);
    getOtp({ email: normalizedEmail });
  });

  const handleLogin = useCallback(
    async (credentials: { otp: string }) => {
      setOtpError(undefined);
      logIn(credentials, async (userId, token) => {
        if (checkFirstTimeUse) {
          await checkFirstTimeUse(token);
        }
        onUserCreated(token);
      });
    },
    [logIn, checkFirstTimeUse, onUserCreated],
  );

  useEffect(() => {
    if (forgotPasswordState.value === 'log in failed') {
      setOtpError("You've entered a wrong OTP. Please try again.");
    }
  }, [forgotPasswordState.value]);

  if (
    forgotPasswordState.value === 'idle' ||
    forgotPasswordState.value === 'requesting otp' ||
    forgotPasswordState.value === 'otp request failed'
  ) {
    const disableForm = forgotPasswordState.value === 'requesting otp';

    return (
      <FullPageForm onSubmit={handleGetOtp}>
        <Input
          autoFocus={true}
          disabled={disableForm}
          type="email"
          label="E-mail"
          placeholder="isaac.newton@university.com"
          {...getOtpForm.register('email', {
            onChange: (event) => {
              const normalizedValue = event.target.value.toLowerCase().trim();
              getOtpForm.setValue('email', normalizedValue);
            },
            onBlur: (event) => {
              const normalizedValue = event.target.value.toLowerCase().trim();
              getOtpForm.setValue('email', normalizedValue);
            },
            required: { value: true, message: 'E-mail is required' },
            validate: validateEmail,
          })}
          readOnly={defaultPF ? true : false}
          error={
            getOtpForm.formState.errors.email?.message ||
            (forgotPasswordState.context.error as string)
          }
        />
        <Spacer height={16} />
        <FooterDiv>
          <ButtonArea>
            <ButtonGroup>
              <Button type="submit" loading={disableForm}>
                Get one-time password
              </Button>

              <LinkArea>
                <P>Wait, I remember. </P>
                <EnhancedLinkButton
                  emailValue={getOtpForm.watch('email')}
                  wiring={wiring.goToLogin}
                >
                  Log in
                </EnhancedLinkButton>
              </LinkArea>
            </ButtonGroup>
          </ButtonArea>
        </FooterDiv>
      </FullPageForm>
    );
  }

  const disableForm =
    forgotPasswordState.value === 'logging in with otp' ||
    forgotPasswordState.value === 'log in succeeded' ||
    authState.name === 'checking if logged in' ||
    authState.name === 'logging in';

  const handleOtpChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (otpError) {
      setOtpError(undefined);
    }
    if (logInForm.formState.errors.otp) {
      logInForm.clearErrors('otp');
    }
    if (forgotPasswordState.context.error) {
      forgotPasswordState.context.error = undefined;
    }
    logInForm.setValue('otp', e.target.value, { shouldValidate: false });
  };

  return (
    <FullPageForm onSubmit={logInForm.handleSubmit(handleLogin)}>
      <P>
        An email has been sent to <b>{getOtpForm.getValues('email')}</b>. Open
        the email, copy the one-time password, and enter it below.
      </P>
      <Spacer height={12} />
      <Input
        autoFocus={true}
        disabled={disableForm}
        {...logInForm.register('otp', {
          required: {
            value: true,
            message: 'One-time password is required',
          },
          minLength: {
            value: 6,
            message: 'One-time password should be 6 numbers',
          },
          maxLength: {
            value: 6,
            message: 'One-time password should be 6 numbers',
          },
          onChange: handleOtpChange,
        })}
        placeholder="6-digit code"
        label="One-time password"
        type="number"
        error={
          otpError ||
          logInForm.formState.errors.otp?.message ||
          (forgotPasswordState.context.error
            ? String(forgotPasswordState.context.error)
            : undefined)
        }
      />
      <Spacer height={16} />
      <FooterDiv>
        <ButtonArea>
          <Button
            type="submit"
            disabled={disableForm}
            loading={disableForm}
            data-agl-cvt={7}
          >
            Log in
          </Button>
          <P>
            Didn’t receive it?{' '}
            <Button
              type="button"
              loading={disableForm}
              css={{ color: theme.colors.blue11 }}
              variant="link"
              onClick={async () => {
                logInForm.reset();
                setOtpError(undefined);

                if (logInForm.formState.errors.otp) {
                  logInForm.clearErrors('otp');
                }
                if (forgotPasswordState.context.error) {
                  forgotPasswordState.context.error = undefined;
                }
                if (lastEmail) {
                  const normalizedEmail = lastEmail.toLowerCase().trim();
                  getOtpForm.reset({ email: normalizedEmail });
                  try {
                    const res = await requestOtp({
                      baseUrl,
                      email: normalizedEmail,
                      domain,
                    });
                    if (res?.status === 200) {
                      showToast({
                        color: 'success',
                        title: 'OTP sent successfully.',
                      });
                    } else {
                      showToast({
                        color: 'danger',
                        title: 'Failed to send OTP. Please try again.',
                      });
                    }
                  } catch (err) {
                    showToast({
                      color: 'danger',
                      title: 'Failed to send OTP. Please try again.',
                    });
                  }
                }
              }}
            >
              Try again
            </Button>
          </P>
        </ButtonArea>
      </FooterDiv>
    </FullPageForm>
  );
};
const LinkArea = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
});

const FooterDiv = styled('div', {
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
});

const FullPageForm = styled('form', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});
FullPageForm.displayName = 'FullPageForm';

const ButtonGroup = styled(Box, {
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  gap: rem(12),
});

const ButtonArea = styled(ButtonGroup, {
  flex: 1,
});
